/**
 * ShellStatusContext
 * ==================
 * Lets a page publish the active analysis's data-confidence up to the AppShell,
 * which sits above the routes and cannot read the per-project ProjectContext
 * below it. ProjectLayout fills this when a project loads; the topbar confidence
 * chip reads it. Same pattern as CoPilotContext (declared above the shell,
 * written by the page, read by the shell).
 */
import React, { createContext, useContext, useState, useCallback } from 'react';

export type DataConfidence = 'high' | 'degraded' | 'low';

export interface ShellStatus {
  confidence: DataConfidence;
  methodologyVersion?: string;
  flagsCount: number;
  defaultsCount: number;
}

interface ShellStatusContextValue {
  status: ShellStatus | null;
  setStatus: (s: ShellStatus | null) => void;
}

const ShellStatusContext = createContext<ShellStatusContextValue | null>(null);

export const ShellStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [status, setStatusState] = useState<ShellStatus | null>(null);
  const setStatus = useCallback((s: ShellStatus | null) => setStatusState(s), []);
  return (
    <ShellStatusContext.Provider value={{ status, setStatus }}>
      {children}
    </ShellStatusContext.Provider>
  );
};

/** Read the shell status (topbar chip). Safe outside the provider: returns null. */
export function useShellStatus(): ShellStatusContextValue {
  return useContext(ShellStatusContext) ?? { status: null, setStatus: () => {} };
}
